import { useState } from 'react';
import { toast } from 'sonner';
import { Cookie, Globe, Smartphone, Target, X, ChevronDown, Zap } from 'lucide-react';
import { cn } from 'helpers/cn';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/Dialogs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useUrlTargeting } from '@/state/experiments/useUrlTargeting';
import { useCookieTargeting } from '@/state/experiments/useCookieTargeting';
import { useDeviceTargeting } from '@/state/experiments/useDeviceTargeting';
import { useActivationTargeting, type ActivationKind } from '@/state/experiments/useActivationTargeting';
import { useSavedTargetings } from '@/state/experiments/useSavedTargetings';
import { ActivationRuleDialog } from './ActivationRuleDialog';
import { SavedTargeting, TargetingSourceLine } from './SavedTargeting';
import { HelpTip, TARGETING_HELP } from './TargetingHelp';

type UrlTargetingProps = {
  experimentId: number;
  /** Beside the saved targetings: a feature flag's Publish rules, for a live flag. */
  headerAction?: React.ReactNode;
  /** Under the intro line: what these rules decide where the card is reused. */
  note?: string;
};

type DragPayload =
  | { type: 'cookie'; label: string }
  | { type: 'activation'; kind: ActivationKind; label: string }
  | { type: 'url'; condition: string; label: string }
  | { type: 'device'; device: string; label: string };

type DraggablePaletteItemProps = { payload: DragPayload; label: string };

type DisclosureProps = {
  value: string;
  /** Names the block for the product tour, as its `data-tour` anchor. */
  tourAnchor: string;
  openValue: string | null;
  onOpenChange: (next: string | null) => void;
  icon: React.ReactNode;
  title: string;
  help: string;
  children: React.ReactNode;
};

type GroupTitleProps = { icon: React.ReactNode; title: string; help: string };

type ChipProps = { children: React.ReactNode; onRemove?: () => void };

const FIELD_CLASS = 'border-line bg-field text-foreground rounded-lg';

function DraggablePaletteItem({ payload, label }: DraggablePaletteItemProps) {
  const onDragStart = (ev: React.DragEvent) => {
    ev.dataTransfer.effectAllowed = 'copy';
    ev.dataTransfer.setData('application/json', JSON.stringify(payload));
  };

  return (
    <button
      type="button"
      draggable
      onDragStart={onDragStart}
      className="border-line bg-field hover:bg-hover text-foreground focus-visible:ring-ring flex w-full cursor-move items-center gap-2 rounded-lg border px-3 py-2 text-left font-mono text-micro transition-colors duration-150 select-none focus-visible:ring-1 focus-visible:outline-none"
    >
      <span className="bg-bar-muted h-3.5 w-1 shrink-0 rounded-pill" />
      <span className="truncate">{label}</span>
    </button>
  );
}

function Disclosure({ value, tourAnchor, openValue, onOpenChange, icon, title, help, children }: DisclosureProps) {
  const isOpen = openValue === value;

  const toggle = () => onOpenChange(isOpen ? null : value);

  return (
    <div className="border-line bg-panel2 rounded-card border px-3" data-tour={tourAnchor}>
      {/* One row: the toggle, the help icon, then the chevron. The help icon is a button, and
          a button cannot sit inside the toggle, so the three are siblings instead. The chevron
          is a second click target for the same toggle and stays out of the tab order. */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={isOpen}
          className="focus-visible:ring-ring flex min-w-0 flex-1 items-center gap-2 py-3 text-left focus-visible:ring-1 focus-visible:outline-none"
        >
          <span className="border-line bg-field text-dim flex size-6 shrink-0 items-center justify-center rounded-lg border">
            {icon}
          </span>
          <span className="micro-label text-foreground">{title}</span>
        </button>
        <HelpTip text={help} label={title} />
        <button
          type="button"
          onClick={toggle}
          tabIndex={-1}
          aria-hidden="true"
          className="flex size-6 shrink-0 items-center justify-center"
        >
          <ChevronDown className={cn('text-dim size-4 transition-transform', isOpen && 'rotate-180')} />
        </button>
      </div>

      {isOpen ? <div className="flex flex-col gap-2 pt-1 pb-3">{children}</div> : null}
    </div>
  );
}

const GroupTitle = ({ icon, title, help }: GroupTitleProps) => (
  <div className="flex items-center gap-2">
    <span className="border-line bg-field text-dim flex size-6 items-center justify-center rounded-lg border">
      {icon}
    </span>
    <span className="micro-label text-foreground">{title}</span>
    <HelpTip text={help} label={title} />
  </div>
);

const Chip = ({ children, onRemove }: ChipProps) => (
  <span className="border-line bg-field text-foreground group/tag hover:bg-hover inline-flex max-w-full items-center gap-1.5 rounded-lg border px-2 py-1 font-mono text-micro transition-colors duration-150">
    <span className="truncate">{children}</span>
    {onRemove ? (
      <Button
        variant="ghost"
        size="xs"
        onClick={onRemove}
        className="text-dim hover:text-danger size-4 shrink-0 rounded p-0 opacity-70 transition-opacity group-hover/tag:opacity-100"
        aria-label="Remove"
      >
        <X className="size-3" strokeWidth={1.5} />
      </Button>
    ) : null}
  </span>
);

export const UrlTargeting = ({ experimentId, headerAction, note }: UrlTargetingProps) => {
  const { URL_TARGETING_CONDITIONS, urlTargeting, addUrlTargeting, deleteUrlTargeting } = useUrlTargeting(experimentId);
  const { cookieTargeting, addCookieTargeting, deleteCookieTargeting } = useCookieTargeting(experimentId);
  const { DEVICE_TYPES, deviceTargeting, addDeviceTargeting, deleteDeviceTargeting } = useDeviceTargeting(experimentId);
  const { activationTargeting, activationAvailable, addActivationTargeting, deleteActivationTargeting } =
    useActivationTargeting(experimentId);
  const savedTargeting = useSavedTargetings(experimentId);

  const [openAccordion, setOpenAccordion] = useState<string | null>('cookies');

  const [activationDialogOpen, setActivationDialogOpen] = useState(false);
  const [activationKind, setActivationKind] = useState<ActivationKind>('event');
  const [activationValue, setActivationValue] = useState('');

  const [isDragOver, setIsDragOver] = useState(false);

  const [cookieDialogOpen, setCookieDialogOpen] = useState(false);
  const [cookieValuesText, setCookieValuesText] = useState('');

  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [newCondition, setNewCondition] = useState<string>('');

  const cookieExistingText = (() => {
    const all = (cookieTargeting || []).flatMap((c: any) => c.cookieValues || []);
    return all.join('\n');
  })();

  const availableDeviceTypes = (() => {
    const selected = new Set((deviceTargeting || []).map((d: any) => d.device));
    return (DEVICE_TYPES || []).filter((t: string) => !selected.has(t));
  })();

  const deviceAddDisabled = (() => {
    const list = deviceTargeting || [];
    const hasAll = list.some((d: any) => d.device === 'All');
    const maxed = list.length >= 3;
    return hasAll || maxed;
  })();

  /**
   * Silent on purpose, and the only catch in this file that is.
   *
   * Anything at all can be dropped on the target area - a file, selected text, a link from
   * another tab - and none of it is JSON. Returning null means the drop is simply ignored,
   * which is the correct reading of "that is not a condition". Reporting it would fire an
   * error at people for dragging the wrong thing two pixels.
   */
  const parseDropPayload = (ev: React.DragEvent): DragPayload | null => {
    try {
      const raw = ev.dataTransfer.getData('application/json');
      if (!raw) return null;
      return JSON.parse(raw) as DragPayload;
    } catch {
      return null;
    }
  };

  const openCookieDialog = () => {
    setCookieValuesText(cookieExistingText);
    setCookieDialogOpen(true);
  };

  const openUrlDialog = (condition: string) => {
    setNewUrl('');
    setNewCondition(condition);
    setUrlDialogOpen(true);
  };

  const openActivationDialog = (kind: ActivationKind) => {
    setActivationKind(kind);
    setActivationValue('');
    setActivationDialogOpen(true);
  };

  const closeActivationDialog = () => {
    setActivationDialogOpen(false);
    setActivationValue('');
  };

  const handleSaveActivation = async () => {
    const value = activationValue.trim();
    if (!value) return;

    try {
      await addActivationTargeting({ kind: activationKind, value });
      closeActivationDialog();
    } catch (error) {
      // The API validates the rule by hand and says what is wrong, so show that and keep the dialog open.
      toast.error(error instanceof Error ? error.message : 'Could not add the activation rule.');
    }
  };

  const handleRemoveActivation = async (id: number) => {
    try {
      await deleteActivationTargeting(id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not remove the activation rule.');
    }
  };

  const handleDrop = async (ev: React.DragEvent) => {
    ev.preventDefault();
    setIsDragOver(false);

    const payload = parseDropPayload(ev);
    if (!payload) return;

    if (payload.type === 'activation') {
      if (activationAvailable) openActivationDialog(payload.kind);
      return;
    }

    if (payload.type === 'cookie') {
      openCookieDialog();
      return;
    }

    if (payload.type === 'url') {
      openUrlDialog(payload.condition);
      return;
    }

    if (payload.type === 'device') {
      // These three rejections were bare `return`s: the chip you dragged simply vanished
      // with no explanation, which looks like the drop zone not working.
      if (deviceAddDisabled) {
        toast.error('Device targeting is limited to three rules, and "All" replaces the rest.');
        return;
      }

      const current = deviceTargeting || [];
      const hasAll = current.some((d: any) => d.device === 'All');

      try {
        if (payload.device === 'All') {
          for (const d of current) {
            await deleteDeviceTargeting(d.id);
          }
          await addDeviceTargeting('All');
          return;
        }

        if (hasAll) {
          toast.error('All devices are already targeted. Remove that rule to target specific types.');
          return;
        }

        if (current.some((d: any) => d.device === payload.device)) {
          toast.error(`${payload.device} is already targeted.`);
          return;
        }

        await addDeviceTargeting(payload.device);
      } catch (error) {
        // Dropping "All" deletes the existing rules before adding it, so a failure part way
        // leaves the panel in a state the user has to see to understand.
        toast.error(error instanceof Error ? error.message : 'Could not change the device targeting.');
      }
    }
  };

  const handleSaveCookie = async () => {
    const values = cookieValuesText
      .split(/\r?\n/)
      .map((v) => v.trim())
      .filter(Boolean);

    if (values.length === 0) return;

    const current = cookieTargeting || [];
    for (const c of current) {
      await deleteCookieTargeting(c.id);
    }

    await addCookieTargeting({ cookieValues: values.join('\n') });

    setCookieDialogOpen(false);
    setCookieValuesText('');
  };

  const handleSaveUrl = async () => {
    if (!newUrl.trim() || !newCondition) return;
    await addUrlTargeting({ url: newUrl.trim(), condition: newCondition });
    setUrlDialogOpen(false);
    setNewUrl('');
    setNewCondition('');
  };

  const cookieChips = (() => {
    const items = cookieTargeting || [];
    const values = items.flatMap((c: any) => c.cookieValues || []);
    return values.map((v: string) => ({ key: `cookie:${v}`, label: v }));
  })();

  const urlChips = (() => {
    return (urlTargeting || []).map((u: any) => ({
      key: `url:${u.id}`,
      id: u.id,
      label: `URL ${u.condition} ${u.url}`,
    }));
  })();

  const deviceChips = (() => {
    return (deviceTargeting || []).map((d: any) => ({
      key: `device:${d.id}`,
      id: d.id,
      label: d.device,
    }));
  })();

  const activationChips = activationTargeting.map((a) => ({
    key: `activation:${a.id}`,
    id: a.id,
    label: `${a.kind === 'event' ? 'Event' : 'Selector'} ${a.value}`,
  }));

  const hasAny = cookieChips.length > 0 || urlChips.length > 0 || deviceChips.length > 0 || activationChips.length > 0;

  return (
    <section className="bg-panel border-line rounded-panel border">
      <div className="border-line flex flex-wrap items-start justify-between gap-2 border-b px-4 py-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="micro-label text-foreground">Targeting &amp; conditions</h2>
            <HelpTip text={TARGETING_HELP.overview} label="targeting and conditions" />
          </div>
          <p className="supporting mt-1">Drag conditions and drop them into the target area on the right.</p>
          {note && <p className="supporting mt-1">{note}</p>}
          <TargetingSourceLine manager={savedTargeting} />
        </div>
        {headerAction ? (
          <div className="flex flex-wrap items-center gap-2">
            {headerAction}
            <SavedTargeting manager={savedTargeting} />
          </div>
        ) : (
          <SavedTargeting manager={savedTargeting} />
        )}
      </div>

      <div className="p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <p className="micro-label">Available conditions</p>

            <div className="flex flex-col gap-2">
              <Disclosure
                value="cookies"
                tourAnchor="settings-targeting-cookie"
                openValue={openAccordion}
                onOpenChange={setOpenAccordion}
                icon={<Cookie className="size-3.5" strokeWidth={1.5} />}
                title="Cookie targeting"
                help={TARGETING_HELP.cookie}
              >
                <DraggablePaletteItem payload={{ type: 'cookie', label: 'Cookie Targeting' }} label="Cookie values" />
              </Disclosure>

              <Disclosure
                value="urls"
                tourAnchor="settings-targeting-url"
                openValue={openAccordion}
                onOpenChange={setOpenAccordion}
                icon={<Globe className="size-3.5" strokeWidth={1.5} />}
                title="URL targeting"
                help={TARGETING_HELP.url}
              >
                {URL_TARGETING_CONDITIONS.map((condition: string) => (
                  <DraggablePaletteItem
                    key={condition}
                    payload={{ type: 'url', condition, label: condition }}
                    label={condition}
                  />
                ))}
              </Disclosure>

              <Disclosure
                value="devices"
                tourAnchor="settings-targeting-device"
                openValue={openAccordion}
                onOpenChange={setOpenAccordion}
                icon={<Smartphone className="size-3.5" strokeWidth={1.5} />}
                title="Device targeting"
                help={TARGETING_HELP.device}
              >
                {availableDeviceTypes.map((d: string) => (
                  <DraggablePaletteItem key={d} payload={{ type: 'device', device: d, label: d }} label={d} />
                ))}
                {availableDeviceTypes.length === 0 ? (
                  <p className="supporting">No more device types available.</p>
                ) : null}

                {deviceAddDisabled ? (
                  <p className="supporting">Device targeting limit reached or All is selected.</p>
                ) : null}
              </Disclosure>

              <Disclosure
                value="activation"
                tourAnchor="settings-targeting-activation"
                openValue={openAccordion}
                onOpenChange={setOpenAccordion}
                icon={<Zap className="size-3.5" strokeWidth={1.5} />}
                title="Activation"
                help={TARGETING_HELP.activation}
              >
                {activationAvailable ? (
                  <>
                    <DraggablePaletteItem
                      payload={{ type: 'activation', kind: 'event', label: 'Event' }}
                      label="Event sent by the site"
                    />
                    <DraggablePaletteItem
                      payload={{ type: 'activation', kind: 'selector', label: 'Selector' }}
                      label="Element on the page"
                    />
                  </>
                ) : (
                  <p className="supporting">Activation is not available for URL Redirect experiments.</p>
                )}
              </Disclosure>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <p className="micro-label">Target area</p>

            <div
              data-tour="settings-targeting-area"
              className={cn(
                'rounded-card min-h-65 border border-dashed p-4 transition-colors duration-150',
                isDragOver ? 'border-sel-line bg-sel-bg' : 'border-line bg-panel2',
              )}
              onDragEnter={(ev) => {
                ev.preventDefault();
                setIsDragOver(true);
              }}
              onDragOver={(ev) => {
                ev.preventDefault();
                ev.dataTransfer.dropEffect = 'copy';
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              role="region"
              aria-label="Conditions drop zone"
            >
              {hasAny ? (
                <div className="flex flex-col gap-4">
                  {cookieChips.length > 0 ? (
                    <div className="flex flex-col gap-2">
                      <GroupTitle
                        icon={<Cookie className="size-3.5" strokeWidth={1.5} />}
                        title="Cookie targeting"
                        help={TARGETING_HELP.cookie}
                      />
                      <div className="flex flex-wrap gap-1.5">
                        {cookieChips.map((c) => (
                          <Chip key={c.key} onRemove={openCookieDialog}>
                            {c.label}
                          </Chip>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {cookieChips.length > 0 && (urlChips.length > 0 || deviceChips.length > 0) ? (
                    <div className="border-line border-t" />
                  ) : null}

                  {urlChips.length > 0 ? (
                    <div className="flex flex-col gap-2">
                      <GroupTitle
                        icon={<Globe className="size-3.5" strokeWidth={1.5} />}
                        title="URL targeting"
                        help={TARGETING_HELP.url}
                      />
                      <div className="flex flex-wrap gap-1.5">
                        {urlChips.map((u) => (
                          <Chip key={u.key} onRemove={() => deleteUrlTargeting(u.id)}>
                            {u.label}
                          </Chip>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {urlChips.length > 0 && deviceChips.length > 0 ? <div className="border-line border-t" /> : null}

                  {deviceChips.length > 0 ? (
                    <div className="flex flex-col gap-2">
                      <GroupTitle
                        icon={<Smartphone className="size-3.5" strokeWidth={1.5} />}
                        title="Device targeting"
                        help={TARGETING_HELP.device}
                      />
                      <div className="flex flex-wrap gap-1.5">
                        {deviceChips.map((d) => (
                          <Chip key={d.key} onRemove={() => deleteDeviceTargeting(d.id)}>
                            {d.label}
                          </Chip>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {activationChips.length > 0 ? (
                    <>
                      {cookieChips.length > 0 || urlChips.length > 0 || deviceChips.length > 0 ? (
                        <div className="border-line border-t" />
                      ) : null}
                      <div className="flex flex-col gap-2">
                        <GroupTitle
                          icon={<Zap className="size-3.5" strokeWidth={1.5} />}
                          title="Activation"
                          help={TARGETING_HELP.activation}
                        />
                        <div className="flex flex-wrap gap-1.5">
                          {activationChips.map((a) => (
                            <Chip key={a.key} onRemove={() => handleRemoveActivation(a.id)}>
                              {a.label}
                            </Chip>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-2 py-10 text-center">
                  <Target
                    className={cn('size-5 transition-colors', isDragOver ? 'text-sel' : 'text-dim')}
                    strokeWidth={1.5}
                  />
                  <p className={cn('font-mono text-micro', isDragOver ? 'text-sel' : 'text-dim')}>
                    {isDragOver ? 'Release to add condition' : 'Drop conditions here.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <ConfirmDialog
          open={cookieDialogOpen}
          title="Set Cookie Targeting"
          onConfirm={handleSaveCookie}
          onCancel={() => {
            setCookieDialogOpen(false);
            setCookieValuesText('');
          }}
          disabled={!cookieValuesText.trim()}
          confirmLabel="Save"
        >
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="supporting flex flex-col gap-2">
              <p>Enter the possible values (strings) you want to track, one per line.</p>
              <p>If any value is found inside accessible cookies, the visitor will be targeted.</p>
              <p>Note: Secure cookies are not accessible to JavaScript.</p>
            </div>
            <Label className="micro-label flex flex-col gap-2">
              <span>Possible values:</span>
              <Textarea
                placeholder={`language=en\nlanguage=se`}
                value={cookieValuesText}
                onChange={(ev) => setCookieValuesText(ev.target.value)}
                className={cn(FIELD_CLASS, 'min-h-48 font-mono text-meta')}
              />
            </Label>
          </div>
        </ConfirmDialog>

        <ActivationRuleDialog
          open={activationDialogOpen}
          kind={activationKind}
          value={activationValue}
          onValueChange={setActivationValue}
          onConfirm={handleSaveActivation}
          onCancel={closeActivationDialog}
        />

        <ConfirmDialog
          open={urlDialogOpen}
          title="Add URL Targeting"
          onConfirm={handleSaveUrl}
          onCancel={() => {
            setUrlDialogOpen(false);
            setNewUrl('');
            setNewCondition('');
          }}
          disabled={!newUrl.trim() || !newCondition}
          confirmLabel="Add"
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label className="micro-label">URL</Label>
              <Input
                value={newUrl}
                onChange={(ev) => setNewUrl(ev.target.value)}
                placeholder="https://example.com/page"
                className={FIELD_CLASS}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label className="micro-label">Condition</Label>
              <Select value={newCondition} onValueChange={setNewCondition}>
                <SelectTrigger className={FIELD_CLASS}>
                  <SelectValue placeholder="Select condition" />
                </SelectTrigger>
                <SelectContent className="border-line bg-panel rounded-lg">
                  {URL_TARGETING_CONDITIONS.map((condition: string) => (
                    <SelectItem key={condition} value={condition}>
                      {condition}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <p className="supporting">
                The condition is preselected when added via drag and drop, but you can change it.
              </p>
            </div>
          </div>
        </ConfirmDialog>
      </div>
    </section>
  );
};
